import { IntermediateShelf } from '../pieces/IntermediateShelf';
import { CustomPiece } from '../pieces/CustomPiece';
import { SupportIntermediateShelf } from '../pieces/SupportIntermediateShelf';
import { Color } from './Color';
import {
  BLACK_COLOR,
  GLASS_COLOR,
  METAL_SUPPORT_IS_REFERENCE,
  NATURAL_COLOR,
  Position,
  STANDARD_COLOR,
  WHITE_COLOR,
} from '../constants';
import { DataStorage, getPiecesData } from '../utils/dataStorage';
import { CompleteIntermediateShelfJson } from '../Data';
import { findColor } from '../utils/colorHelper';
import { ReinforcementIntermediateShelf } from '../pieces/ReinforcementIntermediateShelf';
import { findPieceByRef } from '../utils/helpers';

export class CompleteIntermediateShelf {
  position: number;
  interShelf: CustomPiece<IntermediateShelf>;
  supports: CustomPiece<SupportIntermediateShelf>[];
  reinforcement?: CustomPiece<ReinforcementIntermediateShelf>;
  metalSupport: number; // Only needed for glass IS

  constructor(
    position: number,
    interShelf: CustomPiece<IntermediateShelf>,
    supports: CustomPiece<SupportIntermediateShelf>[],
    reinforcement?: CustomPiece<ReinforcementIntermediateShelf>,
    metalSupport = 0,
  ) {
    this.position = position;
    this.interShelf = interShelf;
    this.supports = supports;
    if (reinforcement) {
      this.reinforcement = reinforcement;
    } else if (CompleteIntermediateShelf.shouldReinforce(interShelf.width)) {
      if (interShelf.color.reference !== GLASS_COLOR) {
        this.reinforcement = ReinforcementIntermediateShelf.standardReinforcementIntermediateShelf(
          supports[0].height,
          interShelf.color,
        );
      }
    }
    this.metalSupport = 0;
    this.updateMetalSupport();
  }

  static shouldReinforce(width: number): boolean {
    return width >= 800;
  }

  get height() {
    if (this.supports.length === 0) return 0;
    return this.supports[0].height;
  }

  set height(value) {
    if (value !== this.height) {
      this.updateSupportIntermediateShelf(Position.Left, value);
      this.updateSupportIntermediateShelf(Position.Right, value);
      if (this.getSupport(Position.Back)) {
        this.updateSupportIntermediateShelf(Position.Back, value);
      } else {
        this.updateReinforcementIntermediateShelf(value);
      }
    }
  }

  get depth() {
    return this.interShelf.depth;
  }

  set depth(value: number) {
    if (value !== this.depth) {
      this.updateIntermediateShelf(undefined, value);
      // this.updateSupportIntermediateShelf(Position.Left, undefined, value);
      // this.updateSupportIntermediateShelf(Position.Right, undefined, value);
    }
  }

  get price() {
    let price = 0;
    for (const item of this.supports) price += item.price;
    if (this.interShelf) price += this.interShelf.price;
    if (this.reinforcement) price += this.reinforcement.price;

    const metalSupportPiece = findPieceByRef(METAL_SUPPORT_IS_REFERENCE);
    if (!metalSupportPiece) throw new Error('Metal support (FR) not in data');
    price += this.metalSupport * metalSupportPiece.price;

    return price;
  }

  get weight() {
    let weight = 0;
    for (const item of this.supports) weight += item.weight;
    if (this.interShelf) weight += this.interShelf.weight;
    if (this.reinforcement) weight += this.reinforcement.weight;

    const metalSupportPiece = findPieceByRef(METAL_SUPPORT_IS_REFERENCE);
    if (!metalSupportPiece) throw new Error('Metal support (FR) not in data');
    weight += this.metalSupport * metalSupportPiece.weight;

    return weight;
  }

  getSupport(
    position: Position,
  ): CustomPiece<SupportIntermediateShelf> | undefined {
    const support = this.supports.find(e => e.position === position);
    // if (!support)
    //   throw new Error(`Couldn't find a support at position ${position}`);
    return support;
  }

  updateIntermediateShelf(
    width?: number,
    depth?: number,
    color?: Color,
    doubleTap?: boolean,
  ) {
    const pieces = getPiecesData();
    if (this.interShelf) {
      const interShelfWidth = width ?? this.interShelf.width;
      const interShelfDepth = depth ?? this.interShelf.depth;
      const interShelfColor = color ?? this.interShelf.color;
      const interShelfDoubleTap = doubleTap ?? this.interShelf.base.doubleTap;
      const searchInfo = IntermediateShelf.getSearchInfos(
        interShelfWidth,
        interShelfDepth,
        interShelfColor,
        interShelfDoubleTap,
      );
      // Update
      this.interShelf = pieces.intermediatesShelves.getCustom(
        searchInfo,
        this.position,
      );

      if (CompleteIntermediateShelf.shouldReinforce(interShelfWidth)) {
        if (this.interShelf.color.reference !== GLASS_COLOR) {
          this.supports = this.supports.filter(
            e => e.position !== Position.Back,
          );
          this.reinforcement = ReinforcementIntermediateShelf.standardReinforcementIntermediateShelf(
            this.height,
            interShelfColor,
          );
        } else {
          if (!this.getSupport(Position.Back)) {
            this.reinforcement = undefined;
            // RTI glass (back support) should always have a width of 420
            // See: https://desk.kewlox.com/browse/CONF-94
            this.supports.push(
              pieces.supportIntermediatesShelves.getCustom(
                SupportIntermediateShelf.getSearchInfos(
                  this.height,
                  420,
                  findColor(GLASS_COLOR),
                ),
                Position.Back,
              ),
            );
          }
        }
      } else {
        this.reinforcement = undefined;
        if (this.getSupport(Position.Back)) {
          this.supports = this.supports.filter(
            e => e.position !== Position.Back,
          );
        }
      }

      if (color && this.reinforcement) {
        let reinforcementColor = findColor(WHITE_COLOR);
        if (
          color.reference === BLACK_COLOR ||
          color.reference === NATURAL_COLOR
        ) {
          reinforcementColor = color;
        }
        this.updateReinforcementIntermediateShelf(
          undefined,
          reinforcementColor,
        );
      }

      if (interShelfColor.reference === GLASS_COLOR) {
        this.updateSupportIntermediateShelf(
          Position.Right,
          undefined,
          undefined,
          findColor(GLASS_COLOR),
        );
        this.updateSupportIntermediateShelf(
          Position.Left,
          undefined,
          undefined,
          findColor(GLASS_COLOR),
        );
      }
    }
    this.updateMetalSupport();
  }

  updateSupportIntermediateShelf(
    position: Position,
    height?: number | null,
    depth?: number | null,
    color?: Color | null,
  ) {
    const support = this.supports.find(e => e.position === position);
    if (support) {
      const supportHeight = height ?? support.height;
      const supportDepth = depth ?? support.depth;
      const supportColor = color ?? support.color;
      const searchInfo = SupportIntermediateShelf.getSearchInfos(
        supportHeight,
        supportDepth,
        supportColor,
      );
      const customP = getPiecesData().supportIntermediatesShelves.getCustom(
        searchInfo,
        position,
      );
      // Update
      this.supports = this.supports.filter(e => e.position !== position);
      this.supports.push(customP);

      if (height !== undefined) {
        this.updateReinforcementIntermediateShelf(height);
      }
    }
    this.updateMetalSupport();
  }

  updateReinforcementIntermediateShelf(
    height?: number | null,
    color?: Color | null,
  ) {
    const reinforcement = this.reinforcement;
    if (reinforcement) {
      const reinforcementHeight = height ?? reinforcement.height;
      const reinforcementColor = color ?? reinforcement.color;
      const searchInfo = ReinforcementIntermediateShelf.getSearchInfos(
        reinforcementHeight,
        reinforcementColor,
      );
      // Update
      this.reinforcement = getPiecesData().reinforcementIntermediatesShelves.getCustom(
        searchInfo,
        Position.Back,
      );
    }
  }

  private updateMetalSupport() {
    /*
    Support in glass should have metal support
    There is 3 metal support needed by support if the depth if >= 420 otherwise 2 are needed
    RTI in glass also need metal supports
     */

    this.metalSupport = 0;
    for (const support of this.supports) {
      if (support.color.reference === GLASS_COLOR) {
        if (support.depth >= 420) {
          this.metalSupport += 3;
        } else this.metalSupport += 2;
      }
    }
  }

  static standardCompleteIntermediateShelf(
    position: number,
    height: number,
    width: number,
    depth: number,
    supportDepth: number,
    colorSupportLeft?: Color | undefined,
    colorSupportRight?: Color | undefined,
    color?: Color,
  ): CompleteIntermediateShelf {
    /**
     * Return a standard Door as custom piece
     */
    if (!color) color = findColor(STANDARD_COLOR);
    const pieces = DataStorage.pieces;
    const searchInfoInterShelf = IntermediateShelf.getSearchInfos(
      width,
      depth,
      color,
    );

    const interShelf = pieces.intermediatesShelves.getCustom(
      searchInfoInterShelf,
      position,
    );

    // Get support
    const supports = [];
    supports.push(
      pieces.supportIntermediatesShelves.getCustom(
        SupportIntermediateShelf.getSearchInfos(
          height,
          supportDepth,
          colorSupportLeft ?? color,
        ),
        Position.Left,
      ),
    );
    supports.push(
      pieces.supportIntermediatesShelves.getCustom(
        SupportIntermediateShelf.getSearchInfos(
          height,
          supportDepth,
          colorSupportRight ?? color,
        ),
        Position.Right,
      ),
    );

    let reinforcement = undefined;
    if (CompleteIntermediateShelf.shouldReinforce(width)) {
      if (color.reference !== GLASS_COLOR) {
        reinforcement = ReinforcementIntermediateShelf.standardReinforcementIntermediateShelf(
          height,
          color,
        );
      } else {
        supports.push(
          pieces.supportIntermediatesShelves.getCustom(
            SupportIntermediateShelf.getSearchInfos(
              height,
              420,
              findColor(GLASS_COLOR),
            ),
            Position.Back,
          ),
        );
      }
    }

    return new CompleteIntermediateShelf(
      position,
      interShelf,
      supports,
      reinforcement,
    );
  }

  static fromModel(model: CompleteIntermediateShelfJson) {
    const { position, interShelf } = model;
    const pieces = getPiecesData();
    const intermediateShelf = CustomPiece.fromModel<IntermediateShelf>(
      interShelf,
      pieces.intermediatesShelves,
    );

    let reinforcement = undefined;
    if (model.reinforcement && Object.keys(model.reinforcement).length > 0) {
      reinforcement = CustomPiece.fromModel<ReinforcementIntermediateShelf>(
        model.reinforcement,
        pieces.reinforcementIntermediatesShelves,
      );
    }

    const supports = [];
    if (model.supports) {
      for (const item of model.supports) {
        supports.push(
          CustomPiece.fromModel<SupportIntermediateShelf>(
            item,
            pieces.supportIntermediatesShelves,
          ),
        );
      }
      supports.sort(CustomPiece.compareTo);
    }
    const metalSupport = model.metalSupport ?? 0;
    return new CompleteIntermediateShelf(
      position,
      intermediateShelf,
      supports,
      reinforcement,
      metalSupport,
    );
  }

  updateColorSupport(position: Position.Left | Position.Right, color: Color) {
    this.updateSupportIntermediateShelf(position, undefined, undefined, color);
  }
}
